import {
  Box,
  Button,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import type { OrgDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import type { ExclusionGroundsSummaryData } from "../summary/QualificationSection";
import { computeEspdRequirement } from "../../utils/computeEspdRequirement";
import { EspdPreviewModal } from "./EspdPreviewModal";
import { EspdThresholdBanner } from "./EspdThresholdBanner";

interface Props {
  event: SourcingEventV2Dto;
  organization: OrgDto;
  exclusionGroundsSummary: ExclusionGroundsSummaryData;
}

export const EspdPreviewSection = ({ event, organization, exclusionGroundsSummary }: Props) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const requirement = computeEspdRequirement(
    event.publicContext.foaPart ?? null,
    event.publicContext.estimatedValue?.value ?? null,
  );

  return (
    <Box mb="6" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="5">
      <Flex align="baseline" justify="space-between" mb="3" gap="4" wrap="wrap">
        <Heading as="h2" size="md">
          {t("ESPD form")}
        </Heading>
        <Button colorScheme="teal" onClick={onOpen}>
          {t("Preview ESPD")}
        </Button>
      </Flex>

      <EspdThresholdBanner requirement={requirement} mb="3" />

      <Text fontSize="xs" color="smMuted" textTransform="uppercase" letterSpacing="wider" mb="2">
        {t("Contains")}
      </Text>
      <List spacing="1" fontSize="sm">
        {[
          `${t("Part I")} — ${t("Procurement data")}`,
          `${t("Part II")} — ${t("Supplier data")}`,
          `${t("Part III")} — ${t("Exclusion grounds")}`,
          `${t("Part IV")} — ${t("Selection criteria")}`,
        ].map((line) => (
          <ListItem key={line}>
            <ListIcon as={CheckCircleIcon} color="smPrimary" />
            {line}
          </ListItem>
        ))}
      </List>

      <EspdPreviewModal
        isOpen={isOpen}
        onClose={onClose}
        event={event}
        organization={organization}
        exclusionGroundsSummary={exclusionGroundsSummary}
      />
    </Box>
  );
};
